import type { ModuleUI, NavSection } from '../module';
import type { IconName } from '../design/icons';
import type { NavArea } from './types';

/** Merge every module's nav into one flat list of sections (same-label sections combine),
 *  sorted by `order`. This is the "41-item sidebar" the Classic skin shows and the input the
 *  area map groups. */
export function mergeNav(modules: ModuleUI[]): NavSection[] {
  const merged = new Map<string, NavSection>();
  for (const s of modules.flatMap((m) => m.nav)) {
    // Same label in the same area combines (finance + portal both add to "Workspace"); the
    // same label in different areas stays apart ("Service desk" at work vs. in Configuration).
    const key = `${s.area ?? ''}::${s.label}`;
    const e = merged.get(key);
    if (e) e.items.push(...s.items);
    else merged.set(key, { ...s, items: [...s.items] });
  }
  // Configuration sections sort after every work section, so a flat sidebar (Classic) reads
  // work first, setup last; inside the Configuration area their own `order` still applies.
  const rank = (s: NavSection) => (s.area === 'config' ? 1000 : 0) + (s.order ?? 100);
  return [...merged.values()].sort((a, b) => rank(a) - rank(b));
}

// The area map turns the 41-item flat sidebar into ~6 top-level AREAS. It is a plain,
// data-driven lookup: NavSection.label → area id, plus per-area metadata (label, icon,
// order). Retune it by editing the two tables below — nothing else in the shell hard-codes
// a module. Sections whose label isn't mapped fall into "More" so nothing is ever lost.

interface AreaDef {
  id: string;
  label: string;
  icon: IconName;
  order: number;
}

/** Top-level areas, in rail order. Keep this list short — that is the whole point. */
export const AREA_DEFS: AreaDef[] = [
  { id: 'home', label: 'Home', icon: 'home', order: 0 },
  { id: 'sales', label: 'Sales', icon: 'trending-up', order: 10 },
  { id: 'service', label: 'Service', icon: 'life-buoy', order: 20 },
  { id: 'projects', label: 'Projects', icon: 'folder-open', order: 30 },
  { id: 'finance', label: 'Finance', icon: 'receipt', order: 40 },
  // Every module's configuration in ONE place, grouped by the same headings as the areas above
  // (Organisation, Customers & sales, Service desk, Projects, Finance, Email, Portal). A module
  // opts a section in with `NavSection.area: 'config'`.
  { id: 'config', label: 'Configuration', icon: 'settings', order: 45 },
  { id: 'admin', label: 'Admin', icon: 'sliders-horizontal', order: 50 },
  { id: 'more', label: 'More', icon: 'more-horizontal', order: 100 },
];

/**
 * NavSection.label (lower-cased) → area id. A section not listed here lands in "more".
 * These labels come from each module's `nav` (docs/modules/*): home→Overview, crm→Customers,
 * quoting→Sales, servicedesk→Service desk, email→Email, projects→Projects,
 * finance→Billing/Delivery/Insight/Workspace, portal→Workspace.
 */
export const SECTION_AREA: Record<string, string> = {
  overview: 'home',
  customers: 'sales',
  sales: 'sales',
  'service desk': 'service',
  email: 'service',
  projects: 'projects',
  billing: 'finance',
  delivery: 'finance',
  insight: 'finance',
  workspace: 'admin',
  // Platform-admin shell sections (rendered only in the isolated /admin engine).
  tenants: 'admin',
  licensing: 'admin',
  announcements: 'admin',
  support: 'admin',
};

const FALLBACK_AREA = 'more';

/** Which area a section belongs to (by its label), defaulting to "More". */
export function areaOf(section: NavSection): string {
  if (section.area && AREA_DEFS.some((a) => a.id === section.area)) return section.area;
  return SECTION_AREA[section.label.trim().toLowerCase()] ?? FALLBACK_AREA;
}

/**
 * Group the merged, sorted nav sections into areas. Only areas that actually have sections
 * are returned, in AREA_DEFS order; section order within an area is preserved.
 */
export function buildAreas(sections: NavSection[]): NavArea[] {
  const byArea = new Map<string, NavSection[]>();
  for (const s of sections) {
    const id = areaOf(s);
    if (!byArea.has(id)) byArea.set(id, []);
    byArea.get(id)!.push(s);
  }
  return AREA_DEFS.filter((a) => byArea.get(a.id)?.length).map((a) => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    order: a.order,
    sections: byArea.get(a.id)!,
  }));
}

/** The area id that owns a given route path (deepest matching nav item wins), or null. */
export function areaForPath(areas: NavArea[], path: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const area of areas) {
    for (const s of area.sections) {
      for (const it of s.items) {
        const to = it.to.split('?')[0]!;
        const matches = it.end ? path === to : path === to || path.startsWith(to + '/');
        if (matches && (!best || to.length > best.len)) best = { id: area.id, len: to.length };
      }
    }
  }
  return best?.id ?? null;
}
